import React, { useEffect, useState } from 'react';
import api from '../../config/axios';
import './OrderCreate.css';
import Header from '../common/Header';
import { useParams, useNavigate } from 'react-router-dom';

interface MenuItem {
  _id: string;
  name: string;
  price: number;
  imageUrl: string;
  description?: string;
}

interface OrderItem {
  menuId: string;
  name: string;
  price: number;
  quantity: number;
  imageUrl: string;
}

const OrderAfterReservation: React.FC = () => {
  const { reservationId } = useParams<{ reservationId: string }>();
  const [menu, setMenu] = useState<MenuItem[]>([]);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [reservation, setReservation] = useState<any>(null);
  const navigate = useNavigate();

  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  useEffect(() => {
    const fetchReservation = async () => {
      try {
        const res = await api.get(`/api/tables/reservations`);
        const found = res.data.find((r: any) => r._id === reservationId && r.user === user?.id);
        if (!found) {
          setError('Không tìm thấy đặt bàn hợp lệ!');
        } else {
          setReservation(found);
        }
      } catch (err) {
        setError('Không thể tải thông tin đặt bàn!');
      }
    };
    if (reservationId && user) fetchReservation();
  }, [reservationId, user]);

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        setLoading(true);
        const res = await api.get('/api/menu');
        setMenu(res.data.data);
      } catch (err) {
        setMenu([]);
      } finally {
        setLoading(false);
      }
    };
    fetchMenu();
  }, []);

  const handleAddItem = (item: MenuItem) => {
    setOrderItems(prev => {
      const found = prev.find(i => i.menuId === item._id);
      if (found) {
        return prev.map(i => i.menuId === item._id ? { ...i, quantity: i.quantity + 1 } : i);
      } else {
        return [...prev, { menuId: item._id, name: item.name, price: item.price, quantity: 1, imageUrl: item.imageUrl }];
      }
    });
  };

  const handleChangeQty = (menuId: string, qty: number) => {
    setOrderItems(prev => prev.map(i => i.menuId === menuId ? { ...i, quantity: qty } : i));
  };

  const handleRemoveItem = (menuId: string) => {
    setOrderItems(prev => prev.filter(i => i.menuId !== menuId));
  };

  const subtotal = orderItems.reduce((sum, i) => sum + i.price * i.quantity, 0);

  const handleSubmit = async () => {
    setError(''); setSuccess('');
    if (!reservationId) {
      setError('Thiếu mã đặt bàn!');
      return;
    }
    if (orderItems.length === 0) {
      setError('Vui lòng chọn ít nhất 1 món!');
      return;
    }
    try {
      setLoading(true);
      await api.patch(`/api/tables/reservations/${reservationId}`, {
        reservedItems: orderItems
      });
      setOrderItems([]);
      setSuccess('Đã lưu món vào đặt bàn!');
      setTimeout(() => navigate('/my-reservations'), 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Lỗi khi lưu món!');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="order-create-bg">
      <Header user={user} onLogout={handleLogout} />
      <div className="order-create-container">
        <div className="order-create-header">
          <button
            className="order-create-back-btn"
            onClick={() => navigate('/')}
            title="Quay lại Dashboard"
          >
            ←
          </button>
          <h2 className="order-create-title">ĐẶT MÓN CHO BÀN ĐÃ ĐẶT</h2>
        </div>
        {reservation && (
          <div style={{ marginBottom: 16, color: '#2d3748' }}>
            <b>Bàn:</b> {reservation.table?.name || 'Không xác định'} | <b>Thời gian:</b> {new Date(reservation.time).toLocaleString()}
          </div>
        )}
        {error && <div className="order-create-message order-create-error">{error}</div>}
        {success && <div className="order-create-message order-create-success">{success}</div>}
        {loading && <div className="order-create-loading">Đang tải...</div>}
        <div className="order-create-main">
          <div className="order-create-left">
            <h3 className="order-create-section-title">Menu</h3>
            <div className="order-create-menu-list">
              {menu.map(item => (
                <div key={item._id} className="order-create-menu-item">
                  <img src={item.imageUrl} alt={item.name} className="order-create-menu-img" />
                  <div className="order-create-menu-info">
                    <div className="order-create-menu-name">{item.name}</div>
                    <div className="order-create-menu-price">{item.price.toLocaleString()} đ</div>
                  </div>
                  <button onClick={() => handleAddItem(item)} className="order-create-menu-add-btn">Thêm</button>
                </div>
              ))}
            </div>
          </div>
          <div className="order-create-right">
            <h3 className="order-create-section-title">Đơn hàng của bạn</h3>
            {orderItems.length === 0 ? (
              <div className="order-create-empty">Chưa chọn món nào</div>
            ) : (
              <table className="order-create-table">
                <thead>
                  <tr>
                    <th>Món</th>
                    <th>Giá</th>
                    <th>Số lượng</th>
                    <th>Thành tiền</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {orderItems.map(item => (
                    <tr key={item.menuId}>
                      <td>{item.name}</td>
                      <td>{item.price.toLocaleString()} đ</td>
                      <td>
                        <input
                          type="number"
                          min={1}
                          value={item.quantity}
                          onChange={e => handleChangeQty(item.menuId, Math.max(1, Number(e.target.value)))}
                          className="order-create-quantity-input"
                        />
                      </td>
                      <td>{(item.price * item.quantity).toLocaleString()} đ</td>
                      <td>
                        <button onClick={() => handleRemoveItem(item.menuId)} className="order-create-remove-btn">X</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="order-create-payment-summary">
              <div className="order-create-summary-item">
                <span className="order-create-summary-label">Tạm tính:</span>
                <span className="order-create-summary-value">{subtotal.toLocaleString()} đ</span>
              </div>
              <div className="order-create-summary-item order-create-summary-total">
                <span className="order-create-summary-label">Tổng cộng:</span>
                <span className="order-create-summary-total-value">{subtotal.toLocaleString()} đ</span>
              </div>
            </div>
            <button
              onClick={handleSubmit}
              className="order-create-submit-btn"
              disabled={loading || orderItems.length === 0}
            >
              {loading ? 'Đang gửi...' : 'Đặt món'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderAfterReservation; 